import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { withinSection } from '../../../shared/components/section-trail';
import { DriverDetailTabs } from '../components/DriverDetailTabs';
import { DriverInviteButton } from '../components/DriverInviteButton';
import { DriverStatusControl } from '../components/DriverStatusControl';
import { DRIVER_TABS, driverTabPath, type DriverTab } from '../drivers.paths';
import { useDriverDetail } from '../hooks/use-driver-detail';
import { useDriverPermissions } from '../hooks/use-driver-permissions';
import { isDriverTabHidden } from '../policies/hidden-driver-tab';

/**
 * A driver's own page: fields and the not-yet-available placeholders, documents and history, each
 * on its own tab like a truck's. Personal data (national ID, date of birth) is behind `driver.view` itself, the
 * access-restriction CLAUDE.md → Data protection asks for — the list screen is what excludes it.
 */
export function DriverDetailPage({ tab }: { tab: DriverTab }) {
  const { id = '' } = useParams();
  const { state, changeStatus, inviteToApp, ...tabActions } = useDriverDetail(id);
  const { canManage, canManageDevices, canViewLedger } = useDriverPermissions();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (isDriverTabHidden(tab, { canManageDevices, canViewLedger })) return <Navigate to={driverTabPath(id, 'details')} replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading driver" breadcrumbs={withinSection('drivers')} tabs={DRIVER_TABS.length} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Driver not found" description="This driver doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this driver" description="Something went wrong loading this driver. Try reloading the page." />;
  }

  const { driver } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('drivers')}
        title={driver.name}
        description={driver.phone}
        actions={
          <div className="flex items-center gap-3">
            <DriverInviteButton isInvitable={canManage && driver.employmentStatus === 'active'} onInvite={inviteToApp} />
            <DriverStatusControl driver={driver} canManage={canManage} onChangeStatus={changeStatus} />
          </div>
        }
      />
      <DriverDetailTabs driver={driver} tab={tab} {...tabActions} />
    </>
  );
}
